import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { webauthn } from '../../platform/webauthn';
import { saveToken } from '../../core/offline/db';

/**
 * Turn any sign-in failure into something a driver can act on.
 *
 * Previously every failure -- a cancelled Face ID prompt, a phone with no
 * passkey, a wrong email, a revoked device -- produced the same sentence.
 * That is unusable in the field and undiagnosable over the phone, because the
 * three most common causes need three completely different responses.
 *
 * The browser reports the ceremony outcome via DOMException.name; the server
 * reports its own outcome as an error code. Both are handled here.
 */
export function describeSignInError(err: unknown): string {
  const e = err as { name?: string; code?: string; message?: string; cause?: { name?: string } };
  const name = e?.name ?? e?.cause?.name;
  const msg = e?.message ?? '';

  // --- The passkey ceremony never completed (no request reached the server) ---
  // Checked BEFORE the browser's own errors: this one is not a device problem
  // at all, and saying "Face ID failed" sends the driver chasing their phone.
  if (msg === 'device_pending_approval') {
    return 'This phone is registered but the office has not approved it yet. '
         + 'Call the office — it takes them a few seconds.';
  }
  if (name === 'NotAllowedError' || e?.code === 'ERROR_CEREMONY_ABORTED') {
    return 'Face ID was cancelled or timed out, or this phone has no passkey for that email. '
         + 'Tap Sign in and complete the prompt. If no prompt appears, this device needs enrolling.';
  }
  if (name === 'SecurityError') {
    return 'This phone would not accept the site as trusted. Make sure the address is '
         + 'app.jwexpressdelivery.com and not a saved shortcut to an older address.';
  }
  if (name === 'NotSupportedError') {
    return 'This phone cannot use passkeys. It needs iOS 16 or later, or Android 9 or later.';
  }
  if (name === 'InvalidStateError') {
    return 'This phone already has a passkey for a different account here. Ask dispatch to '
         + 'revoke the old device before enrolling again.';
  }
  if (name === 'AbortError') return 'Sign-in was interrupted. Try again.';

  // --- The server rejected it ---
  if (msg === 'device_not_authorized') {
    return 'This device is not authorized for your account. If you replaced your phone, '
         + 'ask dispatch to approve it.';
  }
  if (msg === 'phone_not_approved') {
    return 'This phone is not the one approved for your account, so it is on hold. '
         + 'Call the office: they approve the new phone, and the old one stops working.';
  }
  if (msg === 'app_update_required') {
    return 'This copy of the app is out of date. Close the app completely, open it again, '
         + 'and sign in.';
  }
  if (msg === 'authentication_failed') {
    return 'That email does not match an active account, or the passkey did not verify. '
         + 'Check the email is exactly the one dispatch set up for you.';
  }
  if (msg === 'challenge_expired') return 'The sign-in took too long. Tap Sign in again.';
  if (msg.startsWith('http_')) {
    return 'Could not reach the server. Check your signal and try again.';
  }
  return 'Sign-in failed. Use the device enrolled for your account and try again.';
}

/** Login: email → biometric prompt → JWT stored → route screen. */
/*
 * The last email that signed in successfully on this phone.
 *
 * A session lasts an hour, so a driver on a four-hour route signs in four
 * times, and each time they were retyping a full work email one-handed at a
 * loading dock. That is an invitation to write it on the back of the phone.
 *
 * It costs nothing in security. The email is not a secret and never was — it is
 * a username. What actually authenticates is the passkey held in this phone's
 * secure hardware, released only by the driver's own face or PIN, and it cannot
 * be copied to another handset. Someone holding the phone gains a filled-in
 * text box and still cannot get past the biometric.
 *
 * Kept per phone in localStorage, never sent anywhere, and cleared by signing
 * out deliberately.
 */
const LAST_EMAIL = 'jw.lastEmail';

function rememberedEmail(): string {
  try { return localStorage.getItem(LAST_EMAIL) ?? ''; } catch { return ''; }
}

/**
 * Remember the address for next time.
 *
 * Written on a successful SIGN-IN and on a successful ENROLLMENT. Enrollment
 * counts because the server has already matched the address to a real, active
 * person before it will issue a challenge — a typo cannot get this far — and
 * because the trip from "Device enrolled" back to sign-in is the exact point
 * where drivers got lost. Arriving at a sign-in screen with the box already
 * filled is the difference between "carry on" and "what was my email again".
 */
function rememberEmail(clean: string): void {
  try { localStorage.setItem(LAST_EMAIL, clean); } catch { /* fine */ }
}

export function forgetRememberedEmail(): void {
  try { localStorage.removeItem(LAST_EMAIL); } catch { /* nothing to do */ }
}

export function LoginScreen() {
  const [email, setEmail] = useState(rememberedEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Set by the queue when it finds the session dead. See flush.ts.
  const expired = new URLSearchParams(window.location.search).get('expired') === '1';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const clean = email.trim().toLowerCase();
      const result = await webauthn.login(clean);
      await saveToken(result.token);
      // Only after it WORKED. Remembering a typo helps nobody.
      rememberEmail(clean);
      navigate('/route', { replace: true });
    } catch (err) {
      console.error('signin_failed', err);
      setError(describeSignInError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="screen auth-screen">
      <div className="brand-lockup">
        <img className="brand-logo" src="/icons/logo.png"
             alt="JW Express Delivery" width={1274} height={1023} />
        <p className="brand-tagline">Delivering Peace of Mind with Every Package</p>
      </div>
      <h1 className="visually-hidden">JW Express Delivery</h1>
      <p className="subtitle">Driver sign-in</p>
      {/*
        * Sent here mid-shift by an expired session. A driver who is dropped onto
        * a sign-in screen holding a tote needs two things said plainly: why, and
        * that the deliveries they have already captured are safe. Without this
        * the screen reads as "the app lost my work".
        */}
      {expired && (
        <div className="banner warn-banner">
          <strong>Please sign in again.</strong> Your sign-in ran out.
          Nothing is lost — anything you finished is saved on the phone and sends
          itself once you are back in.
          <br />
          <span lang="es">
            <strong>Vuelva a iniciar sesión.</strong> Su sesión expiró.
            No se perdió nada: lo que ya terminó está guardado en el teléfono y
            se enviará solo al volver a entrar.
          </span>
        </div>
      )}
      <form onSubmit={submit} className="col">
        <input className="input" type="email" required autoComplete="username"
               inputMode="email" autoCapitalize="none" autoCorrect="off"
               spellCheck={false}
               placeholder="Work email" value={email}
               onChange={(e) => setEmail(e.target.value)} />
        <button className="btn primary" disabled={busy || !email}>
          {busy ? 'Waiting for biometric…' : 'Sign in with Face ID / fingerprint'}
        </button>
      </form>
      {error && <p className="error">{error}</p>}
      {/* Only shown when there is something to forget. A shared or handed-on
          phone should not carry the last person's address for ever. */}
      {rememberedEmail() !== '' && (
        <button className="btn link" onClick={() => { forgetRememberedEmail(); setEmail(''); }}>
          Not you? Use a different email · ¿No es usted? Usar otro correo
        </button>
      )}
      <Link className="btn link" to="/enroll">First time on this phone? Enroll device</Link>
      {/* The guide is deliberately NOT linked here. It describes how the
          verification works, including the distance the geofence allows, and
          this screen is reachable by anybody on the internet. It lives behind
          the login at /guide instead. */}
    </main>
  );
}

/** Enrollment: creates a PENDING device (D1) — dispatch must approve it. */
export function EnrollScreen() {
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const clean = email.trim().toLowerCase();
      await webauthn.register(clean, label.trim() || 'Driver phone');
      // So the sign-in box is already filled when they come back. See
      // rememberEmail() — the server has already matched this address to a
      // real person, so it cannot be a typo.
      rememberEmail(clean);
      setDone(true);
    } catch (err) {
      console.error('enroll_failed', err);
      setError(describeSignInError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      /*
       * THE STEP THAT LOST PEOPLE.
       *
       * Enrolling does not sign you in: it creates a device the office has to
       * switch on. The old wording ("pending approval. Contact dispatch to
       * activate it") read like a refusal and told the driver to go and chase
       * somebody. In practice the office is told automatically the moment this
       * screen appears, so there is nothing for the driver to chase — and
       * saying so is the difference between a phone call and a wait.
       *
       * Spanish alongside, as on the expired-session banner. This is one of
       * only two screens a driver sees before they can sign in, so it is the
       * worst possible place to assume English.
       */
      <main className="screen auth-screen">
        <h1>Device enrolled</h1>
        <p><strong>Nearly there.</strong> The office has been told and will
           switch this phone on shortly. You do not need to call anyone.</p>
        <p>When it is on, sign in below with your face or fingerprint. Your
           email is already filled in.</p>
        <p lang="es">
          <strong>Ya casi.</strong> Ya avisamos a la oficina y van a activar
          este teléfono en poco tiempo. No necesita llamar a nadie.
          <br />
          Cuando esté activado, inicie sesión abajo con su cara o su huella.
          Su correo ya está escrito.
        </p>
        <Link className="btn primary" to="/login">
          Back to sign-in · Volver a iniciar sesión
        </Link>
      </main>
    );
  }

  return (
    <main className="screen auth-screen">
      <div className="brand-lockup">
        <img className="brand-logo" src="/icons/logo.png"
             alt="JW Express Delivery" width={1274} height={1023} />
      </div>
      <h1>Enroll this device</h1>
      <p className="subtitle">
        Your account will be locked to this phone's biometric. Only one active
        device per driver is allowed.
      </p>
      <form onSubmit={submit} className="col">
        <input className="input" type="email" required placeholder="Work email"
               value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="input" type="text" placeholder="Device name (e.g. Pixel 9)"
               value={label} onChange={(e) => setLabel(e.target.value)} />
        <button className="btn primary" disabled={busy || !email}>
          {busy ? 'Follow the biometric prompt…' : 'Enroll with Face ID / fingerprint'}
        </button>
      </form>
      {error && <p className="error">{error}</p>}
      <Link className="btn link" to="/login">Cancel</Link>
    </main>
  );
}
